import type { ComponentProps } from "react";
import { useSearch } from "@tanstack/react-router";

import type { EventTypePayload } from "@degaus/shared/analytics";

import { useAnalytics } from "@/common/analytics";
import {
  buildOutboundCtaUrl,
  getOutboundEmailAttribution,
} from "@/common/outbound-attribution";
import { WONDATWIN_SIGNUP_URL } from "@/common/wondatwin";

type OutboundCtaPlacement =
  EventTypePayload["outbound_cta_clicked"]["placement"];

type OutboundCtaLinkProps = Omit<ComponentProps<"a">, "href"> & {
  href?: string;
  placement: OutboundCtaPlacement;
};

/**
 * Sends standalone Wonda Twin CTAs to the identity origin while preserving
 * campaign-level email attribution. Non-outbound visits keep the exact same
 * destination and do not emit the outbound-only event.
 */
export function OutboundCtaLink({
  href = WONDATWIN_SIGNUP_URL,
  placement,
  onClick,
  ...props
}: OutboundCtaLinkProps) {
  const search = useSearch({ strict: false });
  const attribution = getOutboundEmailAttribution(search);
  const trackEvent = useAnalytics();

  return (
    <a
      {...props}
      href={buildOutboundCtaUrl(href, attribution)}
      onClick={(event) => {
        onClick?.(event);
        if (event.defaultPrevented || !attribution) return;
        trackEvent("outbound_cta_clicked", {
          // Keep these explicit on the event instead of relying on SDK-derived
          // session properties. The CRM can then attribute the navigation even
          // if the browser leaves before PostHog persists session state.
          utm_source: "outbound",
          utm_medium: "email",
          utm_campaign: attribution.campaign,
          utm_content: attribution.content,
          ...(attribution.term ? { utm_term: attribution.term } : {}),
          placement,
        });
      }}
    />
  );
}
